import React, { useEffect, useRef, useState } from 'react'
import { Box, debounce } from '@mui/material'
import { createChart, type IChartApi, type ISeriesApi, type LineData, type UTCTimestamp } from 'lightweight-charts'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../utils/time-utils'
import { type ExchangeFundingRate } from '../../models/funding-rate/funding-rate-types'
import LiveCrosshairSummary from '../../components/chart/live-crosshair-summary'

interface FundingRateLineChartProps {
  frInterval: string
  chartData: ExchangeFundingRate
  enabledSymbols: string[]
  disabledSymbols: string[]
}

const FundingRateLineChart: React.FC<FundingRateLineChartProps> = (props) => {
  const { frInterval, chartData, enabledSymbols, disabledSymbols } = props

  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [chartObj, setChartObj] = useState<IChartApi | null>()
  const [lineSeriesMap, setLineSeriesMap] = useState<Map<string, ISeriesApi<'Line'>>>(new Map())

  useEffect(
    () => {
      if (chartContainerRef.current === null) {
        return
      }

      const chart: IChartApi = createChart(chartContainerRef.current, {
        layout: {
          textColor: '#fff',
          background: {
            color: '#121212',
          },
        },
        grid: {
          vertLines: {
            color: '#51484f',
          },
          horzLines: {
            color: '#51484f',
          },
        },
        crosshair: {
          mode: 0, // normal
          vertLine: {
            color: '#fff',
            labelBackgroundColor: '#9d5fce',
          },
          horzLine: {
            color: '#fff',
            labelBackgroundColor: '#9d5fce',
          },
        },
        rightPriceScale: {
          autoScale: true,
        },
        timeScale: {
          fixLeftEdge: true,
          fixRightEdge: true,
          borderColor: '#9d5fce',
          timeVisible: true,
          tickMarkFormatter: (time: UTCTimestamp) => formatToLocalMilliTimestampFromUTCNumber(time * 1000),
        },
        localization: {
          timeFormatter: (time: UTCTimestamp) => formatToLocalMilliTimestampFromUTCNumber(time * 1000),
        },
      })
      setChartObj(chart)

      // Ensure the chart is always resized right after the initial render
      chart.resize(
        chartContainerRef.current?.getBoundingClientRect().width,
        chartContainerRef.current?.getBoundingClientRect().height,
      )
      const resizeObserver = new ResizeObserver(debounce((entries: Array<{ contentRect: any }>) => {
        const contentRect = entries[0]?.contentRect
        if (contentRect != null && chart != null) {
          chart.resize(contentRect.width, contentRect.height)
          chart.priceScale('right').applyOptions({
            autoScale: true,
          })
          chart.timeScale().fitContent()
        }
      }))
      resizeObserver.observe(chartContainerRef.current)

      return () => {
        resizeObserver.disconnect()
        chart.remove()
        setChartObj(null)
        setLineSeriesMap(new Map())
      }
    },
    [frInterval],
  )

  useEffect(() => {
    // Note: The effect for disabledSymbols should be executed
    //       BEFORE the effect for enabledSymbols

    if (chartObj == null) {
      setLineSeriesMap(new Map())
      return
    }

    for (const symbol of disabledSymbols) {
      if (lineSeriesMap.has(symbol)) {
        chartObj.removeSeries(lineSeriesMap.get(symbol) as ISeriesApi<'Line'>)
      }
    }

    setLineSeriesMap(prevState => {
      const newMap = new Map()
      for (const key in prevState) {
        if (!disabledSymbols.includes(key)) {
          newMap.set(key, prevState.get(key))
        }
      }
      return newMap
    })
  }, [disabledSymbols])

  useEffect(() => {
    // Note: The effect for enabledSymbols should be executed
    //       AFTER the effect for disabledSymbols

    if (chartObj == null) {
      setLineSeriesMap(new Map())
      return
    }

    const allLineSeries = new Map()

    for (const symbol of enabledSymbols) {
      // If the line series already exists, we don't need to recreate it again
      if (lineSeriesMap.has(symbol)) {
        allLineSeries.set(symbol, lineSeriesMap.get(symbol))
      } else {
        const lineSeries = chartObj.addLineSeries({
          title: symbol,
          color: `#${Math.floor(Math.random() * 2 ** 24).toString(16).padStart(6, '0')}`,
          crosshairMarkerBorderColor: '#fff',
          crosshairMarkerBorderWidth: 1,
          crosshairMarkerBackgroundColor: '#9d5fce',
          priceFormat: {
            type: 'price',
            precision: 3,
            minMove: 0.001,
          },
        })

        const exchange = symbol.split('_')[1].toLowerCase()
        const filtered: LineData[] = chartData[exchange]
          .filter(item => item.symbol_name === symbol)
          .map(item => {
            return {
              time: (item.timestamp / 1000) as UTCTimestamp, // UTCTimestamp expects seconds
              value: Math.round((Number(item.accum_fr) * 10000) * 10) / 10,
            }
          })

        lineSeries.setData(filtered)

        allLineSeries.set(symbol, lineSeries)
      }
    }

    setLineSeriesMap(allLineSeries)
  }, [enabledSymbols])

  return (
    <>
      <LiveCrosshairSummary
        chartObj={chartObj}
        symbols={enabledSymbols}
        lineSeriesMap={lineSeriesMap}
      />

      <Box
        style={{
          width: 'calc(100% - 16px - 300px)',
          height: '100%',
        }}
        ref={chartContainerRef}
      />
    </>
  )
}

export default FundingRateLineChart
